import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  CheckCircle2, ArrowRight, Shield, MessageSquare,
  Star, CreditCard, Gift, Calendar
} from "lucide-react";

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isProfessional = user?.user_type === "professional";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8" data-testid="pricing-page">
        {/* Customer Message - Show that posting is FREE for customers */}
        {user?.user_type === "customer" && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-xl p-8 text-white text-center shadow-xl">
              <div className="flex items-center justify-center gap-2 mb-4">
                <CheckCircle2 className="w-8 h-8" />
                <span className="text-2xl font-bold">FREE for Customers!</span>
              </div>
              <p className="text-lg mb-4 text-white/90">
                Great news! Posting jobs on BQL is <span className="font-bold underline">100% FREE</span> for customers. 
                No subscriptions, no hidden fees — ever.
              </p>
              <p className="text-white/80 mb-6">
                Simply post what you need and get matched with qualified New Jersey professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate("/post-job")}
                  className="bg-white text-emerald-600 hover:bg-white/90 rounded-lg px-8 py-3 text-lg font-bold shadow-md"
                  data-testid="post-job-free-btn"
                >
                  Post a Job for FREE
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-lg px-8 py-3 font-semibold"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Non-logged in visitor message */}
        {!user && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-xl p-6 text-white text-center shadow-lg">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle2 className="w-6 h-6" />
                <span className="text-xl font-bold">Customers Post for FREE!</span>
              </div>
              <p className="text-white/90 mb-4">
                Looking to hire a pro? Posting jobs is 100% free. No subscription needed.
              </p>
              <Button
                onClick={() => navigate("/register/customer")}
                className="bg-white text-emerald-600 hover:bg-white/90 rounded-lg px-6 font-bold"
                data-testid="customer-signup-free-btn"
              >
                Sign Up Free as a Customer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Professional Pricing Section - Hide for customers */}
        {(!user || user?.user_type === "professional") && (
          <>
            {/* Header */}
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-lg text-sm font-semibold mb-6">
                <CreditCard className="w-4 h-4" />
                Professional Pricing
              </span>
              <h1 className="font-bold text-3xl md:text-4xl lg:text-5xl text-[#1e3a5f] mb-4">
                Simple, Transparent Pricing
              </h1>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-4">
                Register free, get your first lead free, then $29.99/month.
              </p>
              <p className="text-sm text-[#d4a853] font-semibold">
                Each lead is shared with only 3 professionals — first come, first served!
              </p>
            </div>

            {/* Main Pricing Card */}
            <div className="max-w-lg mx-auto mb-16">
              <Card className="relative border-2 border-[#d4a853] rounded-xl shadow-xl bg-gradient-to-br from-[#d4a853]/5 to-[#d4a853]/10" data-testid="pricing-card-bql-pro">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-gradient-to-r from-[#d4a853] to-[#e8c484] text-[#1e3a5f] text-sm font-bold rounded-lg shadow-md">
                    BQL Pro
                  </span>
                </div>
                
                <CardContent className="p-8">
                  <h3 className="font-bold text-xl text-[#1e3a5f] mb-4 text-center">How It Works</h3>
                  
                  {/* Pricing Breakdown */}
                  <div className="space-y-4 mb-6">
                    {/* Registration */}
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-green-800">Registration</p>
                        <p className="text-sm text-green-700">Save your card (no charge)</p>
                      </div>
                      <span className="text-2xl font-bold text-green-600">FREE</span>
                    </div>
                    
                    {/* First Lead FREE */}
                    <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Gift className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-emerald-800">First Lead</p>
                        <p className="text-sm text-emerald-700">No charge on your first lead!</p>
                      </div>
                      <span className="text-2xl font-bold text-emerald-600">FREE</span>
                    </div>
                    
                    {/* Monthly Subscription */}
                    <div className="flex items-center gap-4 p-4 bg-[#1e3a5f]/5 rounded-lg border border-[#1e3a5f]/20">
                      <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-[#1e3a5f]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-[#1e3a5f]">Monthly Subscription</p>
                        <p className="text-sm text-slate-600">Starts after first lead</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-[#1e3a5f]">$29.99</span>
                        <span className="text-sm text-slate-500">/mo</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Features List */}
                  <div className="border-t border-slate-200 pt-6 mb-6">
                    <p className="font-semibold text-[#1e3a5f] mb-3">What&apos;s Included:</p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4a853] flex-shrink-0" />
                        <span className="text-slate-700">Unlimited lead access</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4a853] flex-shrink-0" />
                        <span className="text-slate-700">Direct messaging with customers</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4a853] flex-shrink-0" />
                        <span className="text-slate-700">Verified Pro badge on profile</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4a853] flex-shrink-0" />
                        <span className="text-slate-700">Push, email &amp; SMS lead alerts</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4a853] flex-shrink-0" />
                        <span className="text-slate-700">Client management tools</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4a853] flex-shrink-0" />
                        <span className="text-slate-700">Cancel anytime</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* CTA Button */}
                  {user && isProfessional ? (
                    <Button
                      onClick={() => navigate("/pro/dashboard")}
                      className="w-full h-12 bg-[#1e3a5f] hover:bg-[#2d4a6f] rounded-lg font-bold"
                      data-testid="go-to-dashboard-btn"
                    >
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate("/register/professional")}
                      className="w-full h-12 bg-[#d4a853] hover:bg-[#c49843] text-[#1e3a5f] rounded-lg font-bold"
                      data-testid="register-pro-btn"
                    >
                      Register Free
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Trust Signals */}
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white rounded-xl p-6 shadow-sm text-center" data-testid="trust-signal-secure">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">Secure Payments</h3>
                  <p className="text-sm text-slate-500">Powered by Stripe</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm text-center" data-testid="trust-signal-messaging">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">Direct Messaging</h3>
                  <p className="text-sm text-slate-500">Connect instantly with customers</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm text-center" data-testid="trust-signal-quality">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">Quality Leads</h3>
                  <p className="text-sm text-slate-500">Max 3 pros per lead</p>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-white rounded-xl p-8 shadow-sm mb-12">
                <h2 className="font-bold text-2xl text-[#1e3a5f] mb-6 text-center">Frequently Asked Questions</h2>
                <div className="space-y-6 max-w-2xl mx-auto">
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-2">When am I charged?</h3>
                    <p className="text-slate-600">Registration is free and your first lead is free too! Your $29.99/month subscription starts only after you receive your first customer lead.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-2">Can I cancel anytime?</h3>
                    <p className="text-slate-600">Yes! You can cancel your subscription at any time from your Billing page. You&apos;ll continue to have access until the end of your billing period.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-2">What happens if I don&apos;t get any leads?</h3>
                    <p className="text-slate-600">You won&apos;t be charged anything until you receive your first lead (which is free!). Your card is saved during registration but not charged.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-2">How many professionals see each lead?</h3>
                    <p className="text-slate-600">Each lead is shared with a maximum of 3 professionals on a first-come, first-served basis.</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
}
